import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { AppConfigService } from '@com-delta-idp/mnx-utils';
import { of } from 'rxjs';
import { catchError, map, pluck, share } from 'rxjs/operators';
import { ApplicationConfig } from '../../config/config';
import { UrlUtil } from '../../config/urlutil';
import { AirportLookupModel } from '../../model/airport-lookup.model';
import { SearchResult } from '../../model/search-result.model';
import { SearchResponse } from '../../model/search-results.interfaces';
import {
  getCitiesValues,
  getUpdateSearchedCities
} from './search-cities.helper';

declare let delta: any;

@Injectable({
  providedIn: 'root'
})
export class SearchService {
  resultUpdate = new EventEmitter<AirportLookupModel[]>();
  airportUpdate = new EventEmitter<AirportLookupModel[]>();

  private lastSearchKeyword = '';
  private searchedCities: AirportLookupModel[] = [];
  private tempKeyword = '';

  constructor(
    private http: HttpClient,
    private config: ApplicationConfig,
    private appConfigService: AppConfigService,
    private urlUtil: UrlUtil
  ) {}

  getSearchResult(keyword = '') {
    return this.getSearchResultFromAPI(keyword);
  }

  returnMatchFunc(obj, keyword) {
    let matchFound = false;
    for (const value of obj.aliases) {
      if (value.toUpperCase().indexOf(keyword.toUpperCase()) !== -1) {
        matchFound = true;
        break;
      }
    }
    return matchFound;
  }

  matchedCityFunc(obj, keyword) {
    if (
      obj.airportCode.toUpperCase().indexOf(keyword.toUpperCase()) !== -1 ||
      obj.city.toUpperCase().indexOf(keyword.toUpperCase()) !== -1
    )
      return true;

    let result = this.returnMatchFunc(obj, keyword);
    return result;
  }

  getSearchCities(keyword = '', fromDest = '', odPairLookUp = '') {
    let filteredItems: any[] = [];
    if (
      this.searchedCities.length > 0 &&
      keyword.length > 2 &&
      keyword.substr(0, 3).toUpperCase() ===
        this.lastSearchKeyword.toUpperCase()
    ) {
      let _this = this;
      filteredItems = this.searchedCities.filter(function (
        obj: AirportLookupModel
      ) {
        if (!obj.airportCode) {
          const arrCond = [
            {
              condition: () =>
                obj.region.toUpperCase().indexOf(keyword.toUpperCase()) !== -1,
              action: () => true
            },
            {
              condition: () =>
                obj.countryCode.toUpperCase().indexOf(keyword.toUpperCase()) !==
                -1,
              action: () => true
            }
          ];

          let getResult = arrCond.filter((elm) => {
            if (elm.condition()) {
              return elm.action();
            }
          });
          if (getResult.length) return getResult[0].action();

          // if (
          //   obj.countryCode.toUpperCase().indexOf(keyword.toUpperCase()) !==
          //     -1 ||
          //   obj.region.toUpperCase().indexOf(keyword.toUpperCase()) !== -1
          // ) {
          //   return true;
          // }
        } else {
          let result = _this.matchedCityFunc(obj, keyword);
          return result;
        }
        return false;
      });
    }
    if (filteredItems.length > 0) {
      this.resultUpdate.emit(filteredItems);
      // return filteredItems;
    } else {
      if (keyword.length > 3) {
        keyword = keyword.replace(/\s/g, '');
      }
      if (
        keyword.length === 3 &&
        keyword.toUpperCase() !== this.lastSearchKeyword.toUpperCase()
      ) {
        this.searchedCities = [];
        this.lastSearchKeyword = keyword;
        keyword = keyword.replace(/\s/g, '%20');
        if (odPairLookUp === 'true' && fromDest !== '') {
          this.tempKeyword = keyword;
          let airlineCode = this.appConfigService.getConfig('HEADER');
          keyword =
            airlineCode && airlineCode['APP_BASEAIRLINE_CODE'] === 'VS'
              ? keyword
              : keyword.concat('|').concat(fromDest);
        }
        this.retrieveSearchCity(keyword, odPairLookUp, fromDest);
      }
    }
    // return this.searchedCities;
  }

  retrieveSearchCity(keyword, odPairLookUp, fromDest) {
    const newPredictiveAPI =
      this.config.get('APP_NEW_PREDICTIVE_API_URL') ||
      (this.appConfigService.getConfig('HEADER') &&
        this.appConfigService.getConfig('HEADER')[
          'APP_NEW_PREDICTIVE_API_URL'
        ]);
    if (newPredictiveAPI) {
      this.getCities(keyword, newPredictiveAPI).subscribe(
        (data) => {
          if (data && data.length) {
            this.searchedCities = getCitiesValues(data);
            this.resultUpdate.emit(this.searchedCities);
          }
          if (odPairLookUp === 'true' && fromDest !== '') {
            keyword = this.tempKeyword;
            this.tempKeyword = '';
          }
        },
        (err) => {}
      );
    } else {
      this.getSearchCitiesResultFromAPI(keyword).subscribe(
        (data) => {
          if (data.listOfCities) {
            this.updateSearchedCities(data, odPairLookUp, fromDest);
          }
          if (odPairLookUp === 'true' && fromDest !== '') {
            keyword = this.tempKeyword;
            this.tempKeyword = '';
          }
        },
        (err) => {}
      );
    }
  }

  updateSearchedCities(data, odPairLookUp, fromDest) {
    this.searchedCities = getUpdateSearchedCities(data, odPairLookUp, fromDest);
    this.resultUpdate.emit(this.searchedCities);
  }

  getAndUpdateCities(keyword, odPairRestriction = '', fromDest = '') {
    let citiesAttributes: AirportLookupModel[] = [];
    this.getSearchCitiesResultFromAPI(keyword).subscribe(
      (data) => {
        if (data?.listOfCities) {
          citiesAttributes = getUpdateSearchedCities(
            data,
            odPairRestriction,
            fromDest
          );
          this.airportUpdate.emit(citiesAttributes);
        }
      },
      (err) => {}
    );
  }

  getCitiesAttributes(keyword = '', fromDest = '', odPairRestriction = '') {
    keyword = keyword.replace(/\s/g, '');
    this.lastSearchKeyword = keyword;
    keyword = keyword.replace(/\s/g, '%20');
    keyword = keyword.concat('|').concat(fromDest);
    if (this.config.get('APP_NEW_PREDICTIVE_API_URL')) {
      this.getCitiesResult(keyword);
    } else {
      this.getAndUpdateCities(keyword, odPairRestriction, fromDest);
    }
  }

  getCitiesResult(keyword) {
    let citiesAttributes: AirportLookupModel[] = [];
    this.getCities(keyword).subscribe(
      (data) => {
        if (data && data.length) {
          citiesAttributes = getCitiesValues(data);
          this.airportUpdate.emit(citiesAttributes);
        }
      },
      (err) => {}
    );
  }

  getSiteSearchTitle(title: string): string {
    const conditionArr = [
      {
        condition: () =>
          title.indexOf('| Delta') > 0 || title.indexOf('| <b>Delta') > 0,
        action: () => title.slice(0, title.indexOf('| Delta')).trim()
      },
      {
        condition: () =>
          title.indexOf(': Delta') > 0 || title.indexOf(': <b>Delta') > 0,
        action: () => title.slice(0, title.indexOf(': Delta')).trim()
      },
      {
        condition: () => title.indexOf('FR |') == 0,
        action: () => title.replace('FR |', '').trim()
      }
    ];
    let getResult = conditionArr.filter((elm) => {
      if (elm.condition()) {
        return elm.action();
      }
    });
    if (getResult.length) return getResult[0].action();
    return title;
  }

  getSearchResultFromAPI(keyword: string) {
    if (this.config.get('APP_SITESEARCH_GOOGLE_SEARCH_ENABLED') === 'true') {
      if (
        this.config.get('APP_SITESEARCH_GOOGLE_SEARCH_SSR_ENABLED') === 'true'
      ) {
        const url = `${this.config.get('APP_SITESEARCH_GOOGLE_SEARCH_URL')}`;
        const data = { searchTerm: keyword.trim() };
        const customHeaders: HttpHeaders = new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded'
        });
        return this.http
          .post<any>(url, this.urlUtil.toObjQueryString(data), {
            headers: customHeaders
          })
          .pipe(
            pluck('items'),
            map((items: any) => ({
              search_result: items.map((item) => ({
                search_title: this.getSiteSearchTitle(item.htmlTitle),
                search_url: item.link
              }))
            })),
            catchError((err) => of({ search_result: [] })),
            share()
          );
      } else {
        const url = `${this.config.get('APP_SITESEARCH_DIRECT_URL')}`;
        const data = {
          key: `${this.config.get('APP_SITESEARCH_KEY')}`,
          cx: `${this.config.get('APP_SITESEARCH_CX')}`,
          q: keyword.trim(),
          fields:
            'promotions(title,htmlTitle,link,bodyLines/title,bodyLines/htmlTitle),searchInformation(totalResults,formattedTotalResults),items(title,htmlTitle,link,snippet,htmlSnippet)'
        };
        let headers = new HttpHeaders();
        headers = headers.set(
          'Content-Type',
          'application/json; charset=UTF-8'
        );

        return this.http
          .get<SearchResponse>(url, { headers: headers, params: data })
          .pipe(
            map((response: SearchResponse) => {
              return this.setSiteSearchResultsView(response);
            }),
            catchError((err) => of([]))
          );
      }
    } else {
      return this.http
        .get<any>(
          `${this.config.get(
            'APP_SITESEARCH_PREDICTIONS_URL'
          )}?query_string=${keyword}`
        )
        .pipe(share());
    }
  }

  setSiteSearchResultsView(searchResponse: SearchResponse) {
    let searchPromotionsResults = this.searchPromotionsMap(searchResponse);
    let searchItemResults = this.searchItemsMap(searchResponse);
    let searchResults = [];
    searchPromotionsResults.forEach((item) => {
      searchResults.push(item);
    });
    searchItemResults.forEach((item) => {
      searchResults.push(item);
    });
    return searchResults;
  }

  searchPromotionsMap(searchResponse: SearchResponse) {
    return searchResponse?.promotions
      ? (searchResponse?.promotions.map((item) => {
          return {
            title: this.getSiteSearchTitle(item.title),
            url: item.link
          } as SearchResult;
        }) as SearchResult[])
      : [];
  }

  searchItemsMap(searchResponse: SearchResponse) {
    return searchResponse?.items
      ? (searchResponse.items.map((item) => {
          return {
            title: this.getSiteSearchTitle(item.title),
            url: item.link
          } as SearchResult;
        }) as SearchResult[])
      : [];
  }

  getSearchCitiesResultFromAPI(keyword: string) {
    // TODO convert to common http client
    return this.http
      .get<any>(
        `${this.config.get('APP_PREDICTIVE_CITIES_URL')}?code=${keyword}`
      )
      .pipe(share());
  }

  getCities(keyword: string, url = '') {
    // TODO convert to common http client
    const apiURL = url || `${this.config.get('APP_NEW_PREDICTIVE_API_URL')}`;
    return this.http.get<any>(`${apiURL}/${keyword}`).pipe(share());
  }
}
